// Fitting cubic Béziers to points (DRAW_PLAN §5, Pencil and Simplify): Philip J. Schneider's algorithm
// ("An Algorithm for Automatically Fitting Digitized Curves", Graphics Gems, 1990) — least-squares fit with
// chord-length parameters, Newton–Raphson reparameterisation, split at the worst point when the error is too
// large. Corners (a turn sharper than `cornerDeg`) split the input first so they stay sharp.
import type { Anchor, Subpath } from '../model/types';

type P = [number, number];
const sub = (a: P, b: P): P => [a[0] - b[0], a[1] - b[1]];
const add = (a: P, b: P): P => [a[0] + b[0], a[1] + b[1]];
const mulS = (a: P, s: number): P => [a[0] * s, a[1] * s];
const dot = (a: P, b: P) => a[0] * b[0] + a[1] * b[1];
const len = (a: P) => Math.hypot(a[0], a[1]);
const norm = (a: P): P => {
  const l = len(a) || 1;
  return [a[0] / l, a[1] / l];
};
type Cubic = [P, P, P, P];

const bez = (c: Cubic, t: number): P => {
  const u = 1 - t;
  return add(
    add(mulS(c[0], u * u * u), mulS(c[1], 3 * u * u * t)),
    add(mulS(c[2], 3 * u * t * t), mulS(c[3], t * t * t)),
  );
};
const bez1 = (c: Cubic, t: number): P => {
  const u = 1 - t;
  return add(
    add(mulS(sub(c[1], c[0]), 3 * u * u), mulS(sub(c[2], c[1]), 6 * u * t)),
    mulS(sub(c[3], c[2]), 3 * t * t),
  );
};
const bez2 = (c: Cubic, t: number): P =>
  add(
    mulS(add(sub(c[2], mulS(c[1], 2)), c[0]), 6 * (1 - t)),
    mulS(add(sub(c[3], mulS(c[2], 2)), c[1]), 6 * t),
  );

function chordParams(pts: P[]): number[] {
  const u = [0];
  for (let i = 1; i < pts.length; i++) u.push(u[i - 1]! + len(sub(pts[i]!, pts[i - 1]!)));
  const total = u[u.length - 1] || 1;
  return u.map((v) => v / total);
}

function generate(pts: P[], u: number[], t1: P, t2: P): Cubic {
  const first = pts[0]!,
    last = pts[pts.length - 1]!;
  let c00 = 0,
    c01 = 0,
    c11 = 0,
    x0 = 0,
    x1 = 0;
  for (let i = 0; i < pts.length; i++) {
    const t = u[i]!,
      v = 1 - t;
    const b0 = v * v * v,
      b1 = 3 * v * v * t,
      b2 = 3 * v * t * t,
      b3 = t * t * t;
    const a1 = mulS(t1, b1),
      a2 = mulS(t2, b2);
    c00 += dot(a1, a1);
    c01 += dot(a1, a2);
    c11 += dot(a2, a2);
    const tmp = sub(pts[i]!, add(mulS(first, b0 + b1), mulS(last, b2 + b3)));
    x0 += dot(a1, tmp);
    x1 += dot(a2, tmp);
  }
  const det = c00 * c11 - c01 * c01;
  let al = det ? (x0 * c11 - x1 * c01) / det : 0;
  let ar = det ? (c00 * x1 - c01 * x0) / det : 0;
  const segLen = len(sub(last, first));
  const eps = 1e-6 * segLen;
  if (al < eps || ar < eps) al = ar = segLen / 3; // Wu/Barsky heuristic
  return [first, add(first, mulS(t1, al)), add(last, mulS(t2, ar)), last];
}

function maxError(pts: P[], c: Cubic, u: number[]): { err: number; at: number } {
  let err = 0,
    at = Math.floor(pts.length / 2);
  for (let i = 1; i < pts.length - 1; i++) {
    const d = len(sub(bez(c, u[i]!), pts[i]!));
    if (d > err) {
      err = d;
      at = i;
    }
  }
  return { err, at };
}

function reparam(pts: P[], u: number[], c: Cubic): number[] {
  return u.map((t, i) => {
    const d = sub(bez(c, t), pts[i]!);
    const d1 = bez1(c, t),
      d2 = bez2(c, t);
    const den = dot(d1, d1) + dot(d, d2);
    return den ? Math.min(1, Math.max(0, t - dot(d, d1) / den)) : t;
  });
}

function fitCubic(pts: P[], t1: P, t2: P, error: number, out: Cubic[], depth = 0) {
  if (pts.length === 2) {
    const d = len(sub(pts[1]!, pts[0]!)) / 3;
    out.push([pts[0]!, add(pts[0]!, mulS(t1, d)), add(pts[1]!, mulS(t2, d)), pts[1]!]);
    return;
  }
  let u = chordParams(pts);
  let c = generate(pts, u, t1, t2);
  let { err, at } = maxError(pts, c, u);
  if (err < error) {
    out.push(c);
    return;
  }
  if (err < error * 4) {
    for (let k = 0; k < 20; k++) {
      u = reparam(pts, u, c);
      c = generate(pts, u, t1, t2);
      ({ err, at } = maxError(pts, c, u));
      if (err < error) {
        out.push(c);
        return;
      }
    }
  }
  if (depth > 24 || pts.length < 3) {
    out.push(c);
    return;
  }
  const center = norm(sub(pts[at - 1]!, pts[at + 1]!));
  fitCubic(pts.slice(0, at + 1), t1, center, error, out, depth + 1);
  fitCubic(pts.slice(at), mulS(center, -1), t2, error, out, depth + 1);
}

/** Drops points closer than `minDist` to the previous kept point (pointer jitter). */
function dedupe(pts: P[], minDist: number): P[] {
  const out: P[] = [];
  for (const p of pts) if (!out.length || len(sub(p, out[out.length - 1]!)) >= minDist) out.push(p);
  if (out.length === 1 && pts.length > 1) out.push(pts[pts.length - 1]!);
  return out;
}

/** Indices where the polyline turns sharper than `deg` (measured over a short window). */
function corners(pts: P[], deg: number, closed: boolean): number[] {
  const out: number[] = [];
  const n = pts.length;
  for (let i = closed ? 0 : 1; i < (closed ? n : n - 1); i++) {
    const a = pts[(i - 1 + n) % n]!,
      b = pts[i]!,
      c = pts[(i + 1) % n]!;
    const v1 = norm(sub(b, a)),
      v2 = norm(sub(c, b));
    const ang = (Math.acos(Math.max(-1, Math.min(1, dot(v1, v2)))) * 180) / Math.PI;
    if (ang > deg) out.push(i);
  }
  return out;
}

/**
 * A subpath through `input` (world or local points) within `error` (same units), keeping corners sharper than
 * `cornerDeg`. `closed`: the result is closed (pencil end near its start).
 */
export function fitPoints(input: P[], error: number, closed = false, cornerDeg = 60): Subpath {
  let pts = dedupe(input, error / 4);
  if (closed && pts.length > 2 && len(sub(pts[0]!, pts[pts.length - 1]!)) < error) pts = pts.slice(0, -1);
  if (pts.length < 2) {
    const p = pts[0] ?? [0, 0];
    return {
      closed: false,
      points: [{ x: p[0], y: p[1], inX: p[0], inY: p[1], outX: p[0], outY: p[1], type: 'corner' }],
    };
  }
  const cs = corners(pts, cornerDeg, closed);
  // Pieces between corners (closed: rotate to start at a corner, or treat as one loop).
  let pieces: P[][];
  if (closed) {
    if (cs.length) {
      const rot = [...pts.slice(cs[0]!), ...pts.slice(0, cs[0]! + 1)];
      const rc = cs.map((c) => (c - cs[0]! + pts.length) % pts.length).concat(pts.length);
      pieces = [];
      for (let k = 0; k < rc.length - 1; k++) pieces.push(rot.slice(rc[k], rc[k + 1]! + 1));
    } else pieces = [[...pts, pts[0]!]];
  } else {
    const idx = [0, ...cs, pts.length - 1];
    pieces = [];
    for (let k = 0; k < idx.length - 1; k++) pieces.push(pts.slice(idx[k], idx[k + 1]! + 1));
  }
  const cubics: Cubic[] = [];
  for (const piece of pieces) {
    if (piece.length < 2) continue;
    const m = piece.length;
    let t1 = norm(sub(piece[Math.min(1, m - 1)]!, piece[0]!)),
      t2 = norm(sub(piece[Math.max(0, m - 2)]!, piece[m - 1]!));
    if (closed && !cs.length) {
      // A smooth loop: the tangent at the seam is the same both ways.
      const tan = norm(sub(piece[1]!, piece[m - 2]!));
      t1 = tan;
      t2 = mulS(tan, -1);
    }
    fitCubic(piece, t1, t2, error, cubics);
  }
  // Cubics → anchors (shared endpoints merged).
  const anchors: Anchor[] = [];
  cubics.forEach((c, i) => {
    if (i === 0)
      anchors.push({
        x: c[0][0],
        y: c[0][1],
        inX: c[0][0],
        inY: c[0][1],
        outX: c[1][0],
        outY: c[1][1],
        type: 'corner',
      });
    else {
      const prev = anchors[anchors.length - 1]!;
      prev.outX = c[1][0];
      prev.outY = c[1][1];
    }
    anchors.push({
      x: c[3][0],
      y: c[3][1],
      inX: c[2][0],
      inY: c[2][1],
      outX: c[3][0],
      outY: c[3][1],
      type: 'corner',
    });
  });
  if (closed && anchors.length > 1) {
    // The last anchor is the first again: fold its in-handle into the first and drop it.
    const last = anchors.pop()!;
    anchors[0]!.inX = last.inX;
    anchors[0]!.inY = last.inY;
  }
  // Anchors whose handles are collinear are smooth.
  for (const a of anchors) {
    const v1: P = [a.x - a.inX, a.y - a.inY],
      v2: P = [a.outX - a.x, a.outY - a.y];
    if (len(v1) > 1e-9 && len(v2) > 1e-9 && dot(norm(v1), norm(v2)) > 0.999) a.type = 'smooth';
  }
  return { closed, points: anchors };
}

/** Samples a subpath into a polyline (for Simplify), `step` apart along each segment. */
export function sampleSubpath(sp: Subpath, perSeg = 16): P[] {
  const out: P[] = [];
  const n = sp.points.length;
  const segs = sp.closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const a = sp.points[i]!,
      b = sp.points[(i + 1) % n]!;
    const c: Cubic = [
      [a.x, a.y],
      [a.outX, a.outY],
      [b.inX, b.inY],
      [b.x, b.y],
    ];
    for (let k = 0; k < perSeg; k++) out.push(bez(c, k / perSeg));
  }
  if (!sp.closed && n) out.push([sp.points[n - 1]!.x, sp.points[n - 1]!.y]);
  return out;
}

/** Simplify (DRAW_PLAN §5): refit within `tolerance`, corners kept. */
export function simplifySubpath(sp: Subpath, tolerance: number): Subpath {
  if (sp.points.length < 3) return sp;
  const out = fitPoints(sampleSubpath(sp), tolerance, sp.closed, 45);
  // Simplify never adds anchors: a path that is already lean stays as it is.
  return out.points.length < sp.points.length ? out : sp;
}
